import { useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { X, MapPin, CreditCard, Clock, ShoppingCart, MessageSquare, Navigation, MessageCircle, AlertTriangle, CheckCircle2, ExternalLink, Copy, Check, ChevronDown, Printer, RotateCcw, RefreshCw, UserRound, Landmark, TicketPercent, Building2, ArrowRightLeft } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { OrderStatusBadge } from './order-status-badge';
import { DetailSection } from './detail-section';
import { OrderDeliverySection } from '@/components/delivery/order-delivery-section';
import { ordersClient, getForwardStatus, canCancel, canSwitchPaymentMethod, orderCustomerName, type Order, type OrderStatus, type OrderPaymentAttempt, type SwitchPaymentMethodResult } from '@/lib/orders-client';
import { ChannelBadge } from './channel-badge';
import { TransferVerificationDisplay } from './transfer-verification-display';
import { OrderTicketPrint } from './order-ticket-print';
import { PrintStatusChip } from './print-status-chip';
import { canRequestReprint, offersThermalReprint, printSkipReasonOf, printStatusOf } from './print-status';
import { useReprint } from './use-reprint';
import { LoyaltyRedemptionRow } from './loyalty-redemption-row';
import { PaymentHoldNotice } from './payment-hold-notice';
import { SwitchPaymentMethodDialog } from './switch-payment-method-dialog';
import { needsTransferReview, TRANSFER_REVIEW_TINT } from './transfer-review';
import { useBrandHasPos } from './use-transfer-review';
import type { OrderPaymentActions } from './use-order-payment-actions';
import { getDashboardUrl } from '@/components/theme-provider';
import { ScopeChips } from '@/components/scope-chips';
import { splitApartmentFromInstructions } from '@/lib/address-display';
import { appliedDiscountList } from '@/lib/discounts-client';
import { loyaltyRedemptionOf } from '@/lib/loyalty-redemption';
import { format } from 'date-fns';

// Backend payment-preference enum → 'orders' locale key (payment.methods.*).
const PAYMENT_METHOD_KEYS: Record<string, string> = {
    cash: 'cash',
    send_pos_for_card: 'sendPosForCard',
    bank_transfer: 'bankTransfer',
    mercado_pago: 'mercadoPago',
    card: 'card',
    pay_at_store: 'payAtStore',
};

/**
 * What the items came to before the coupon came off.
 *
 * Derived rather than read off the order: `order.total` is already net of the discount, the delivery
 * fee and the tip, and no pre-discount figure is persisted. Line subtotals already include their
 * option deltas, so summing them is the whole of it.
 */
function itemsSubtotal(order: Order): number {
    return order.items.reduce((sum, item) => sum + Number(item.subtotal), 0);
}

// pending → confirm, confirmed → complete (mirrors orders-client's getForwardActionLabel).
function forwardActionKey(status: OrderStatus): string | null {
    if (status === 'pending') return 'actions.confirm';
    if (status === 'confirmed') return 'actions.complete';
    return null;
}

export interface ViewConversationOptions {
    /** Also open the conversation's cart panel — the order just went back to a draft there. */
    openCart?: boolean;
}

interface OrderDetailPanelProps {
    order: Order;
    onClose: () => void;
    onStatusChange: (orderId: string, newStatus: OrderStatus) => void;
    onViewConversation?: (conversationId: string, options?: ViewConversationOptions) => void;
    /** Retry the POS push of a paid order parked in pos_push_failed. */
    onRetryPosPush?: (orderId: string) => Promise<void>;
    /** Open the customer's profile (addresses + order history). Omit to keep the name static. */
    onViewCustomer?: () => void;
    /** Open the bank transfer that paid this order. Omit to hide the action. */
    onViewTransfer?: () => void;
    /** Rendered inside a dialog (customer profile, transfers page) instead of as the side panel. */
    embedded?: boolean;
    /** Line sessions of a multi-branch brand: show which sucursal the order belongs to. */
    showLocation?: boolean;
    /** The surface's shared payment actions: switching an unpaid order's method, and hold refusals. */
    paymentActions?: OrderPaymentActions;
    /**
     * A storefront draft held for its transfer (the board knows; the order read does not): confirm the
     * transfer by hand, which places it. The host owns the confirmation dialog. Omit to hide.
     */
    onPlaceHeldTransfer?: () => void;
    /** That request is in flight. */
    placingHeldTransfer?: boolean;
}

export function OrderDetailPanel({ order, onClose, onStatusChange, onViewConversation, onRetryPosPush, onViewCustomer, onViewTransfer, embedded, showLocation, paymentActions, onPlaceHeldTransfer, placingHeldTransfer }: OrderDetailPanelProps) {
    const { t } = useTranslation('orders');
    // Normalised once: an order read during the deploy window still answers the singular snapshot.
    const appliedDiscounts = appliedDiscountList(order);
    // Loyalty money (cashback, reward coupon…) — inside `discountAmount`, never in `appliedDiscounts`.
    const loyaltyRedemption = loyaltyRedemptionOf(order);
    // The redemption row already links its customer; the payment section only links one it does not.
    const paymentLoyaltyCustomerId =
        order.loyaltyCustomerId && loyaltyRedemption?.loyaltyCustomerId !== order.loyaltyCustomerId
            ? order.loyaltyCustomerId
            : undefined;
    // The discounts' share of the delivery fee (C4) — struck out of the fee line below.
    const deliveryFeeDiscount = Number(order.deliveryFeeDiscountAmount ?? 0);
    // Gross fee: the totals block adds it back so a discount's fee share has something to come off.
    const deliveryFee = order.deliveryType === 'delivery' ? Number(order.deliveryFee ?? 0) : 0;
    // Items alone make up the total: a breakdown that restates it would be noise.
    const showBreakdown =
        deliveryFee > 0 || appliedDiscounts.length > 0 || !!loyaltyRedemption || Number(order.tipAmount) > 0;
    const dashboardUrl = getDashboardUrl();
    /** True for exactly as long as the counter ticket is being handed to `window.print()`. */
    const [printing, setPrinting] = useState(false);
    // Thermal printing: shown once a printer was involved; the browser print then says what it is.
    const reprintable = canRequestReprint(order);
    const thermalPrinting = reprintable || offersThermalReprint(order) || printStatusOf(order).kind !== 'none';
    // Skipped at acceptance (POS connected, no printers…): no chip, no reprint, one muted line saying why.
    const printSkipReason = printSkipReasonOf(order);
    const browserPrintLabel = thermalPrinting ? t('print.browser') : t('ticket.action');
    const { reprint, reprintingId } = useReprint();
    const isPosPushFailed = order.status === 'pos_push_failed';
    // A POS brand already gets the warning in its POS note; only a brand with none needs it here.
    const brandHasPos = useBrandHasPos(order.brandId);
    const transferReview = brandHasPos === false && needsTransferReview(order);
    const actionKey = forwardActionKey(order.status);
    const showStatusActions = !isPosPushFailed && (actionKey || canCancel(order.status) || onPlaceHeldTransfer);
    const hasConversationLink = !!(order.conversationId && onViewConversation);
    const [switchingPayment, setSwitchingPayment] = useState(false);
    const canSwitchPayment = !!paymentActions && canSwitchPaymentMethod(order);
    // The order stopped being unpaid underneath an open dialog: drop it, or it would reopen on the next one.
    useEffect(() => {
        if (!canSwitchPayment) setSwitchingPayment(false);
    }, [canSwitchPayment]);
    // A refusal is only worth showing while it still describes the order: "ya está pago" stays, the
    // in-flight ones go once the order has left pending_payment.
    const holdNotice =
        paymentActions?.notice?.orderId === order.id &&
        (order.status === 'pending_payment' || paymentActions.notice.code === 'PAYMENT_ALREADY_SETTLED')
            ? paymentActions.notice
            : null;

    const handleSwitched = (result: SwitchPaymentMethodResult) => {
        setSwitchingPayment(false);
        // Blocked: the order is a draft again, and the cart is where its blockers get fixed.
        if (result.outcome === 'blocked' && order.conversationId && onViewConversation) {
            onViewConversation(order.conversationId, { openCart: true });
        }
    };

    return (
        <div
            className={`${
                embedded
                    ? // Sized by the host dialog's flex column. Not `h-full`: a percentage height
                      // against an auto-height dialog collapses, which kills the inner scroll.
                      'w-full min-h-0 flex-1'
                    : // Mobile: cover the order list as a full overlay; desktop: fixed side column.
                      'absolute inset-0 z-20 w-full h-full md:static md:z-auto md:w-[26rem] md:shrink-0 md:border-l md:border-border'
            } bg-card flex flex-col overflow-hidden`}
        >
            {/* Header — which order, where it came from, and the panel's own tools. */}
            <div className="border-b border-border px-4 py-3 shrink-0">
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <h2 className="text-base font-semibold text-foreground truncate">
                            {t('detail.title', { id: order.id.slice(0, 8) })}
                        </h2>
                        <p className="mt-0.5 text-xs text-muted-foreground tabular-nums">
                            {t('detail.createdAt', {
                                date: format(new Date(order.createdAt), 'dd/MM/yyyy HH:mm'),
                            })}
                        </p>
                    </div>
                    <div className="flex items-center gap-1 -mr-1.5">
                        {/* T31's print/receipt quick action, on the detail surface as well as the board card. */}
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setPrinting(true)}
                            className="h-8 w-8 text-muted-foreground hover:text-foreground"
                            aria-label={browserPrintLabel}
                            title={browserPrintLabel}
                        >
                            <Printer className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={onClose}
                            className="h-8 w-8 text-muted-foreground hover:text-foreground"
                            aria-label={t('detail.closePanel')}
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
                {/* Status first, then where the order was placed. The channel sits here rather than
                    in a row of its own because this panel IS what three surfaces show about an order
                    (the board, the customer profile, the transfers page), and the board card it is
                    opened from leads with the same chip. */}
                <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                    <OrderStatusBadge status={order.status} />
                    <ChannelBadge
                        channel={order.channel}
                        originConversationId={order.originConversationId}
                        size="md"
                        className="shrink-0"
                    />
                    {order.isSandbox && (
                        <span className="shrink-0 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-300">
                            {t('sandboxBadge')}
                        </span>
                    )}
                </div>
                {/* Which brand/branch this order belongs to: brand + branch in aggregate, branch alone for a multi-branch brand. */}
                <ScopeChips
                    brandName={order.brandName}
                    locationName={order.locationName}
                    lineLocation={showLocation}
                    pendingLocationLabel={order.locationId ? null : t('branchPending')}
                    className="mt-2"
                />
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-4 divide-y divide-border">
                {/* Paid but never registered with the POS — guided manual resolution */}
                {isPosPushFailed && (
                    <div className="py-4">
                        <PosPushFailedActionCard
                            order={order}
                            dashboardUrl={dashboardUrl}
                            onStatusChange={onStatusChange}
                            onRetryPosPush={onRetryPosPush}
                            onViewConversation={onViewConversation}
                        />
                    </div>
                )}

                {/* An unverified receipt placed this order: with no POS note to carry the warning,
                    the store reads it here before it starts cooking. */}
                {transferReview && (
                    <div className="py-4">
                        <div
                            role="status"
                            className={`flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium ${TRANSFER_REVIEW_TINT}`}
                        >
                            <Landmark className="h-4 w-4 mt-0.5 shrink-0" />
                            <span>{t('transferReview.banner')}</span>
                        </div>
                    </div>
                )}

                {/* Thermal printing skipped at acceptance: the branch cannot print, and says why. */}
                {!thermalPrinting && printSkipReason && (
                    <DetailSection icon={Printer} title={t('print.title')}>
                        <p className="text-xs text-muted-foreground">
                            {t(`print.skipped.${printSkipReason}`, { defaultValue: t('print.skipped.fallback') })}
                        </p>
                    </DetailSection>
                )}

                {/* Thermal printing — one line per ticket, and the reprint when the store accepted it. */}
                {thermalPrinting && (
                    <DetailSection
                        icon={Printer}
                        title={t('print.title')}
                        action={
                            reprintable ? (
                                <Button
                                    variant={
                                        ['failed', 'unsent'].includes(printStatusOf(order).kind) ? 'default' : 'outline'
                                    }
                                    size="sm"
                                    className="h-7 gap-1.5 text-xs"
                                    disabled={reprintingId === order.id}
                                    onClick={() => void reprint(order.id)}
                                >
                                    <RotateCcw className="h-3.5 w-3.5" />
                                    {t('print.reprint')}
                                </Button>
                            ) : undefined
                        }
                    >
                        <PrintStatusChip printing={order.printing} variant="full" />
                    </DetailSection>
                )}

                {/* Customer */}
                <DetailSection icon={UserRound} title={t('detail.sections.customer')}>
                    {onViewCustomer ? (
                        <button
                            type="button"
                            onClick={onViewCustomer}
                            className="group block w-full rounded-md text-left focus-visible:outline-2 focus-visible:outline-ring"
                            title={t('detail.viewCustomerProfile')}
                        >
                            <div className="text-sm font-medium text-foreground group-hover:underline">
                                {orderCustomerName(order) || t('list.noName')}
                            </div>
                            <div className="text-sm text-muted-foreground tabular-nums">{order.customer?.phone}</div>
                            <span className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary group-hover:underline">
                                <UserRound className="h-3 w-3" />
                                {t('detail.viewCustomerProfile')}
                            </span>
                        </button>
                    ) : (
                        <>
                            <div className="text-sm font-medium text-foreground">
                                {orderCustomerName(order) || t('list.noName')}
                            </div>
                            <div className="text-sm text-muted-foreground tabular-nums">{order.customer?.phone}</div>
                        </>
                    )}
                    {/* Link to conversation */}
                    {hasConversationLink && (
                        <Button
                            variant="outline"
                            size="sm"
                            className="mt-2.5 w-full"
                            onClick={() => onViewConversation!(order.conversationId!)}
                        >
                            <MessageSquare className="h-4 w-4" />
                            {t('detail.viewConversation')}
                        </Button>
                    )}
                </DetailSection>

                {/* Items */}
                <DetailSection icon={ShoppingCart} title={t('detail.sections.items')}>
                    <ul className="space-y-2.5">
                        {order.items.map((item, idx) => (
                            <li key={idx} className="flex gap-2.5">
                                <span className="flex h-6 min-w-6 shrink-0 items-center justify-center rounded-md bg-muted px-1.5 text-xs font-semibold text-foreground tabular-nums">
                                    {item.quantity}x
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className="flex justify-between gap-3 text-sm">
                                        <span className="font-medium text-foreground">{item.name}</span>
                                        <span className="shrink-0 text-muted-foreground tabular-nums">
                                            {order.currency} {Number(item.subtotal).toFixed(2)}
                                        </span>
                                    </div>
                                    {item.options && item.options.length > 0 && (
                                        <div className="mt-0.5 text-xs text-muted-foreground space-y-0.5">
                                            {Object.entries(
                                                item.options.reduce<Record<string, typeof item.options>>((acc, opt) => {
                                                    (acc[opt.groupName] ??= []).push(opt);
                                                    return acc;
                                                }, {})
                                            ).map(([groupName, opts]) => (
                                                <div key={groupName}>
                                                    <span className="font-medium text-muted-foreground">{groupName}: </span>
                                                    {opts.map((o, i) => (
                                                        <span key={i}>
                                                            {o.optionName}
                                                            {o.priceDelta !== 0 && (
                                                                <span className="text-muted-foreground/70"> ({o.priceDelta > 0 ? '+' : ''}{o.priceDelta.toFixed(2)})</span>
                                                            )}
                                                            {i < opts.length - 1 ? ', ' : ''}
                                                        </span>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {item.comments && (
                                        <div className="mt-0.5 text-xs text-muted-foreground italic">
                                            {typeof item.comments === 'string'
                                                ? item.comments
                                                : item.comments.map((c, i) => (
                                                    <div key={i}>
                                                        {c.quantity}x {c.text}
                                                    </div>
                                                ))}
                                        </div>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>

                    {/* Order-wide notes — kept next to the items they qualify, not buried under the total */}
                    {order.notes && (
                        <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-800/60 dark:bg-amber-950/40">
                            <MessageCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-700 dark:text-amber-300" />
                            <div className="min-w-0 text-sm">
                                <span className="block text-xs font-medium text-amber-800 dark:text-amber-300">
                                    {t('detail.notes')}
                                </span>
                                <span className="text-amber-900 dark:text-amber-200">{order.notes}</span>
                            </div>
                        </div>
                    )}
                </DetailSection>

                {/* Delivery */}
                {order.deliveryType && (
                    <DetailSection icon={MapPin} title={t('detail.sections.fulfillment')}>
                        <div className="text-sm font-medium text-foreground">
                            {order.deliveryType === 'delivery' ? t('detail.delivery') : t('detail.pickup')}
                        </div>
                        {order.deliveryAddress && (
                            <DeliveryAddressDisplay address={order.deliveryAddress} />
                        )}
                        {order.deliveryType === 'delivery' && order.deliveryFee != null && (
                            <div className="mt-1.5 text-sm text-muted-foreground">
                                {t('detail.deliveryFee')}
                                {(order.deliveryAddress?.deliveryZoneName as string | undefined)
                                    ? ` (${order.deliveryAddress!.deliveryZoneName as string})`
                                    : ''}
                                :{' '}
                                {deliveryFeeDiscount > 0 && (
                                    <s className="mr-1.5 text-muted-foreground/70">
                                        {order.currency} {Number(order.deliveryFee).toFixed(2)}
                                    </s>
                                )}
                                <span className="text-foreground tabular-nums">
                                    {Number(order.deliveryFee) - deliveryFeeDiscount > 0
                                        ? `${order.currency} ${(Number(order.deliveryFee) - deliveryFeeDiscount).toFixed(2)}`
                                        : t('detail.free')}
                                </span>
                            </div>
                        )}
                        {order.estimatedDeliveryMinutes && (
                            <div className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
                                <Clock className="h-3.5 w-3.5" />
                                {t('detail.estimatedMinutes', { minutes: order.estimatedDeliveryMinutes })}
                            </div>
                        )}
                    </DetailSection>
                )}

                {/* The rider (delivery provider): status, driver, tracking, and the operator's actions. */}
                <OrderDeliverySection order={order} />

                {/* Payment */}
                {(order.paymentPreference || (order.paymentAttempts && order.paymentAttempts.length > 0)) && (
                    <DetailSection
                        icon={CreditCard}
                        title={t('detail.sections.payment')}
                        action={
                            order.paymentPreference && order.paymentConfirmedAt ? (
                                <Badge variant="success">
                                    <CheckCircle2 />
                                    {t('detail.paid')}
                                </Badge>
                            ) : undefined
                        }
                    >
                        {order.paymentPreference && (
                            <div className="space-y-2.5">
                                <div className="text-sm font-medium text-foreground">
                                    {PAYMENT_METHOD_KEYS[order.paymentPreference]
                                        ? t(`payment.methods.${PAYMENT_METHOD_KEYS[order.paymentPreference]}`)
                                        : order.paymentPreference}
                                </div>

                                {/* Menteo payment link (awaiting payment) */}
                                {order.status === 'pending_payment' && order.paymentLinkUrl && (
                                    <PaymentLinkRow url={order.paymentLinkUrl} deadlineAt={order.paymentDeadlineAt} />
                                )}

                                {/* Transfer Verification */}
                                {order.paymentPreference === 'bank_transfer' && order.bankTransfer && (
                                    <div className="space-y-2">
                                        <TransferVerificationDisplay
                                            flush
                                            transfer={order.bankTransfer}
                                            currency={order.currency}
                                            getReceiptUrl={() => ordersClient.getTransferReceiptUrl(order.id).then((r) => r.data)}
                                        />
                                        {/* Same detail (and attribution fixes) the Bank Transfers page shows */}
                                        {onViewTransfer && (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="w-full"
                                                onClick={onViewTransfer}
                                            >
                                                <Landmark className="h-3.5 w-3.5" />
                                                {t('detail.viewTransfer')}
                                            </Button>
                                        )}
                                    </div>
                                )}

                                {/* Cross-links to the dashboard payment + loyalty profile */}
                                {dashboardUrl && (order.menteoPaymentId || paymentLoyaltyCustomerId) && (
                                    <div className="flex flex-col gap-1">
                                        {order.menteoPaymentId && (
                                            <a
                                                href={`${dashboardUrl}/payments/transactions/${order.menteoPaymentId}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                                            >
                                                <ExternalLink className="h-3 w-3" />
                                                {t('detail.viewPaymentInDashboard')}
                                            </a>
                                        )}
                                        {paymentLoyaltyCustomerId && (
                                            <a
                                                href={`${dashboardUrl}/loyalty/customers/${paymentLoyaltyCustomerId}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                                            >
                                                <ExternalLink className="h-3 w-3" />
                                                {t('detail.viewLoyaltyCustomer')}
                                            </a>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Payment Attempts */}
                        {order.paymentAttempts && order.paymentAttempts.length > 0 && (
                            <div className={order.paymentPreference ? 'mt-3' : undefined}>
                                <PaymentAttemptsSection attempts={order.paymentAttempts} />
                            </div>
                        )}
                    </DetailSection>
                )}

                {/* Total (fee, tip and coupon are already folded into it — broken out so the column
                    adds up: items + gross fee − discounts + tip = total) */}
                <div className="py-4">
                    <div className="rounded-xl border border-border bg-muted/30 p-3 space-y-1.5">
                        {showBreakdown && (
                            <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">{t('detail.itemsSubtotal')}</span>
                                <span className="text-muted-foreground tabular-nums">
                                    {order.currency} {itemsSubtotal(order).toFixed(2)}
                                </span>
                            </div>
                        )}
                        {/* Gross, not net: each discount line below carries its own fee share, so
                            netting it here as well would take the fee off twice. */}
                        {deliveryFee > 0 && (
                            <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">{t('detail.deliveryFee')}</span>
                                <span className="text-muted-foreground tabular-nums">
                                    {order.currency} {deliveryFee.toFixed(2)}
                                </span>
                            </div>
                        )}
                        {/* One line per frozen snapshot, each at ITS own amount — an order may carry
                            several discounts, and a single summed line would not say which gave what. */}
                        {appliedDiscounts.map((discount, index) => (
                            <div
                                key={discount.discountId || `${discount.code ?? discount.name ?? ''}-${index}`}
                                className="flex justify-between gap-3 text-sm"
                            >
                                <span className="inline-flex min-w-0 flex-wrap items-center gap-1.5 text-muted-foreground">
                                    <TicketPercent className="h-3.5 w-3.5 text-muted-foreground/70" />
                                    {/* A codeless automatic promotion is named, not coded. */}
                                    <span className="font-medium text-foreground">
                                        {discount.code ?? discount.name ?? '—'}
                                    </span>
                                    {/* An applied coupon taking nothing off is the confusing case an
                                        operator has to be able to see, so it is labelled rather than hidden. */}
                                    {!(Number(discount.discountAmount) > 0) && (
                                        <span className="text-xs text-muted-foreground">
                                            {t('detail.couponNoEffect')}
                                        </span>
                                    )}
                                    {/* Its fee share: included in the amount, taken off the fee line above. */}
                                    {Number(discount.deliveryFeeDiscountAmount) > 0 && (
                                        <span className="text-xs text-muted-foreground">
                                            {Number(discount.deliveryFeeDiscountAmount) >= Number(order.deliveryFee ?? 0)
                                                ? t('detail.feeShareFree')
                                                : t('detail.feeShare', {
                                                      amount: `${order.currency} ${Number(discount.deliveryFeeDiscountAmount).toFixed(2)}`,
                                                  })}
                                        </span>
                                    )}
                                </span>
                                {Number(discount.discountAmount) > 0 && (
                                    <span className="shrink-0 font-medium text-emerald-700 dark:text-emerald-400 tabular-nums">
                                        -{order.currency} {Number(discount.discountAmount).toFixed(2)}
                                    </span>
                                )}
                            </div>
                        ))}
                        {/* Its own line, traced to the loyalty transaction and customer: the coupon
                            rows above never include it, so without it the column would not add up. */}
                        {loyaltyRedemption && (
                            <LoyaltyRedemptionRow
                                redemption={loyaltyRedemption}
                                formatMoney={(amount) => `${order.currency} ${amount.toFixed(2)}`}
                                dashboardUrl={dashboardUrl}
                            />
                        )}
                        {Number(order.tipAmount) > 0 && (
                            <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">{t('detail.tip')}</span>
                                <span className="text-muted-foreground tabular-nums">
                                    {order.currency} {Number(order.tipAmount).toFixed(2)}
                                </span>
                            </div>
                        )}
                        <div className={`flex items-baseline justify-between ${showBreakdown ? 'border-t border-border pt-2' : ''}`}>
                            <span className="font-semibold text-foreground">{t('detail.total')}</span>
                            <span className="text-lg font-semibold text-foreground tabular-nums">
                                {order.currency} {Number(order.total).toFixed(2)}
                            </span>
                        </div>
                    </div>
                </div>

                {/* History: dates, cancellation, POS outcome and tracking */}
                <DetailSection icon={Clock} title={t('detail.sections.history')}>
                    <div className="space-y-1 text-xs text-muted-foreground tabular-nums">
                        <div>
                            {t('detail.createdAt', {
                                date: format(new Date(order.createdAt), 'dd/MM/yyyy HH:mm'),
                            })}
                        </div>
                        {order.confirmedAt && (
                            <div>
                                {t('detail.confirmedAt', {
                                    date: format(new Date(order.confirmedAt), 'dd/MM/yyyy HH:mm'),
                                })}
                            </div>
                        )}
                    </div>

                    {/* Cancelled reason */}
                    {order.cancelledReason && (
                        <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 dark:border-red-800/60 dark:bg-red-950/40">
                            <span className="block text-xs font-medium text-red-800 dark:text-red-300">
                                {t('detail.cancelledReason')}
                            </span>
                            <div className="text-sm text-red-700 dark:text-red-300">{order.cancelledReason}</div>
                        </div>
                    )}

                    {/* POS Error (the pos_push_failed action card already shows it) */}
                    {order.posError && !isPosPushFailed && (
                        <div className="mt-3 rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800/60 px-3 py-2 space-y-1">
                            <div className="flex items-center gap-1.5">
                                <AlertTriangle className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
                                <span className="text-sm font-medium text-red-800 dark:text-red-300">{t('detail.posError')}</span>
                            </div>
                            <div className="text-xs text-red-700 dark:text-red-300 break-words">{order.posError}</div>
                        </div>
                    )}

                    {/* Tracking / External IDs */}
                    {(order.trackingUrl || order.externalPosOrderId) && (
                        <div className="mt-3 space-y-1.5">
                            {order.externalPosOrderId && (
                                <div className="text-xs text-muted-foreground">
                                    POS: {order.externalPosProvider && `${order.externalPosProvider} — `}#{order.externalPosOrderId}
                                </div>
                            )}
                            {order.trackingUrl && (
                                <a
                                    href={order.trackingUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                                >
                                    <Navigation className="h-3 w-3" />
                                    {t('detail.viewTracking')}
                                </a>
                            )}
                        </div>
                    )}
                </DetailSection>
            </div>

            {/* Footer — the order's next step, always in reach. Cancelling sits apart, on the left,
                as an outline so it is never the button the eye (or the thumb) lands on first.
                pos_push_failed is resolved through its dedicated action card above. */}
            {/* A refused cancel/switch, said right above the buttons that were refused. */}
            {holdNotice && (
                <div className="shrink-0 border-t border-border bg-card px-4 pt-3">
                    <PaymentHoldNotice code={holdNotice.code} />
                </div>
            )}
            {showStatusActions && (
                <div className={`flex shrink-0 items-center gap-2 bg-card px-4 py-3 ${holdNotice ? '' : 'border-t border-border'}`}>
                    {canCancel(order.status) && (
                        <Button
                            variant="outline"
                            className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800 dark:border-red-800/60 dark:text-red-300 dark:hover:bg-red-950/40 dark:hover:text-red-200"
                            onClick={() => onStatusChange(order.id, 'cancelled')}
                        >
                            {t('actions.cancelOrder')}
                        </Button>
                    )}
                    {onPlaceHeldTransfer && (
                        <Button className="flex-1" onClick={onPlaceHeldTransfer} disabled={placingHeldTransfer}>
                            <CheckCircle2 className="h-4 w-4" />
                            {t('board.heldTransfer.action')}
                        </Button>
                    )}
                    {actionKey && (
                        <Button
                            className="flex-1"
                            onClick={() => onStatusChange(order.id, getForwardStatus(order)!)}
                        >
                            <CheckCircle2 className="h-4 w-4" />
                            {t(actionKey)}
                        </Button>
                    )}
                    {/* Not a status move: the order is released and placed again on the new method. */}
                    {canSwitchPayment && (
                        <Button variant="outline" className="flex-1" onClick={() => setSwitchingPayment(true)}>
                            <ArrowRightLeft className="h-4 w-4" />
                            {t('actions.switchPayment')}
                        </Button>
                    )}
                </div>
            )}

            {paymentActions && (
                <SwitchPaymentMethodDialog
                    // Closes as soon as the order stops being unpaid — it may update before onSwitched runs.
                    order={switchingPayment && canSwitchPayment ? order : null}
                    notice={paymentActions.notice}
                    onSwitch={paymentActions.switchPaymentMethod}
                    onSwitched={handleSwitched}
                    onDismiss={() => setSwitchingPayment(false)}
                />
            )}

            {/* Off-screen until the print medium — see the `@media print` block in globals.css. */}
            <OrderTicketPrint order={printing ? order : null} onPrinted={() => setPrinting(false)} />
        </div>
    );
}

/**
 * Guided resolution for a PAID order that never reached the POS: the operator contacts the
 * customer manually, then retries the push, keys the order into the POS by hand and marks it so
 * (that IS the order's final success status on a POS brand — there is no acceptance queue in
 * Menteo for it to fall back to), or cancels it and refunds the captured payment from the dashboard.
 */
function PosPushFailedActionCard({
    order,
    dashboardUrl,
    onStatusChange,
    onRetryPosPush,
    onViewConversation,
}: {
    order: Order;
    dashboardUrl: string | undefined;
    onStatusChange: (orderId: string, newStatus: OrderStatus) => void;
    onRetryPosPush?: (orderId: string) => Promise<void>;
    onViewConversation?: (conversationId: string) => void;
}) {
    const { t } = useTranslation('orders');
    const [retrying, setRetrying] = useState(false);
    const hasConversation = !!(onViewConversation && order.conversationId);
    const paymentUrl =
        dashboardUrl && order.menteoPaymentId
            ? `${dashboardUrl}/payments/transactions/${order.menteoPaymentId}`
            : null;

    const retry = async () => {
        if (!onRetryPosPush) return;
        setRetrying(true);
        try {
            await onRetryPosPush(order.id);
        } finally {
            setRetrying(false);
        }
    };

    const cancelAndRefund = () => {
        // Open the dashboard payment page first (same-click, so popup blockers allow it) — the
        // refund is confirmed there; cancelling the order here never refunds by itself.
        if (paymentUrl) window.open(paymentUrl, '_blank', 'noopener,noreferrer');
        onStatusChange(order.id, 'cancelled');
    };

    return (
        <div role="alert" className="rounded-xl bg-red-50 dark:bg-red-950/40 border border-red-300 dark:border-red-800/60 p-3.5 space-y-2.5">
            <div className="flex items-center gap-1.5">
                <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
                <span className="text-sm font-semibold text-red-800 dark:text-red-300">{t('posFailed.actionRequired')}</span>
            </div>
            <p className="text-xs text-red-800 dark:text-red-300">
                <Trans
                    i18nKey="posFailed.description"
                    ns="orders"
                    components={{ bold: <span className="font-semibold" /> }}
                />
            </p>
            {order.posError && (
                <div className="rounded bg-red-100/70 dark:bg-red-900/40 border border-red-200 dark:border-red-800/60 px-2 py-1.5 space-y-0.5">
                    <div className="text-[11px] text-red-700 dark:text-red-300 break-words">{order.posError}</div>
                    {order.posErrorPermanent && (
                        <div className="text-[10px] font-medium text-red-800 dark:text-red-300">
                            {t('posFailed.permanentError')}
                        </div>
                    )}
                </div>
            )}
            <p className="text-xs text-red-800 dark:text-red-300">
                {hasConversation ? t('posFailed.step1WithConversation') : t('posFailed.step1')}
                <br />
                {t('posFailed.step2')}
            </p>
            <div className="flex flex-col gap-1.5">
                {onRetryPosPush && (
                    <Button size="sm" onClick={() => void retry()} disabled={retrying}>
                        <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${retrying ? 'animate-spin' : ''}`} />
                        {retrying ? t('posFailed.retrying') : t('posFailed.retry')}
                    </Button>
                )}
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onStatusChange(order.id, 'pushed_to_external')}
                    disabled={retrying}
                >
                    {t('posFailed.alreadyInPos')}
                </Button>
                <Button variant="destructive" size="sm" onClick={cancelAndRefund} disabled={retrying}>
                    {t('posFailed.cancelAndRefund')}
                </Button>
            </div>
            {paymentUrl ? (
                <p className="text-[10px] text-red-700/80 dark:text-red-300">{t('posFailed.refundNoteAutoOpen')}</p>
            ) : (
                <p className="text-[10px] text-red-700/80 dark:text-red-300">{t('posFailed.refundNoteManual')}</p>
            )}
        </div>
    );
}

function DeliveryAddressDisplay({ address }: { address: Record<string, unknown> }) {
    const { t } = useTranslation('orders');
    const addr = address as {
        raw?: string;
        coordinates?: { lat: number; lng: number };
        street?: string;
        neighborhood?: string;
        city?: string;
        state?: string;
        zipCode?: string;
        country?: string;
        apartment?: string;
        instructions?: string;
        source?: string;
    };

    // The apartment matters more to a driver here than the postal code, so it gets its own field —
    // lifted out of the note when the web checkout folded it in there (see `address-display.ts`).
    const { apartment, instructions } = splitApartmentFromInstructions(addr);

    const hasCoordinates = addr.coordinates?.lat != null && addr.coordinates?.lng != null;
    const mapsUrl = hasCoordinates
        ? `https://www.google.com/maps/search/?api=1&query=${addr.coordinates!.lat}%2C${addr.coordinates!.lng}`
        : null;

    const isGeolocation = addr.source === 'geolocation';

    // Build display address from structured fields or fall back to raw
    const displayAddress = addr.raw || [addr.street, addr.neighborhood, addr.city].filter(Boolean).join(', ') || JSON.stringify(address);

    return (
        <div className="mt-1 space-y-1.5">
            {/* Main address */}
            <div className="text-sm text-foreground">{displayAddress}</div>

            {/* Apartment — its own field, ahead of the postal code and never inside the comment */}
            {apartment && (
                <div className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted px-2 py-1">
                    <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">{t('detail.address.apartment')}</span>
                    <span className="text-sm font-semibold text-foreground">{apartment}</span>
                </div>
            )}

            {/* Structured details (if geocoded and different from raw) */}
            {isGeolocation && addr.street && addr.city && addr.raw !== addr.street && (
                <div className="text-xs text-muted-foreground/70 space-y-0.5">
                    {addr.neighborhood && (
                        <div>
                            {t('detail.address.neighborhood')}: {addr.neighborhood}
                        </div>
                    )}
                    {addr.zipCode && (
                        <div>
                            {t('detail.address.zipCode')}: {addr.zipCode}
                        </div>
                    )}
                </div>
            )}

            {/* Delivery instructions — the comment alone, with the apartment lifted out of it */}
            {instructions && (
                <div className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-300">
                    <MessageCircle className="h-3 w-3 mt-0.5 shrink-0" />
                    <span>{instructions}</span>
                </div>
            )}

            {/* Google Maps link */}
            {mapsUrl && (
                <a
                    href={mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                >
                    <Navigation className="h-3 w-3" />
                    {t('detail.viewOnMaps')}
                </a>
            )}
        </div>
    );
}

/** Copyable Menteo payment link + its deadline, shown while the order awaits payment. */
function PaymentLinkRow({ url, deadlineAt }: { url: string; deadlineAt?: string }) {
    const { t } = useTranslation('orders');
    const [copied, setCopied] = useState(false);

    const copy = () => {
        void navigator.clipboard.writeText(url).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div className="rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/60 p-2.5 space-y-1.5">
            <div className="flex items-center justify-between gap-2">
                <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-medium text-primary hover:underline truncate"
                    title={url}
                >
                    {url}
                </a>
                <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={copy} aria-label={t('paymentLink.copy')}>
                    {copied ? <Check className="h-3 w-3 text-emerald-600 dark:text-emerald-400" /> : <Copy className="h-3 w-3" />}
                </Button>
            </div>
            {deadlineAt && (
                <div className="text-[11px] text-amber-800 dark:text-amber-300">
                    {t('paymentLink.expires', {
                        date: format(new Date(deadlineAt), 'dd/MM/yyyy HH:mm'),
                    })}
                </div>
            )}
        </div>
    );
}

// labelKey resolves against attempts.events.* in the 'orders' namespace.
const ATTEMPT_EVENT_CONFIG: Record<OrderPaymentAttempt['event'], { labelKey: string; color: string; iconColor: string }> = {
    approved: { labelKey: 'approved', color: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300', iconColor: 'text-emerald-500 dark:text-emerald-400' },
    paid: { labelKey: 'paid', color: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300', iconColor: 'text-emerald-500 dark:text-emerald-400' },
    declined: { labelKey: 'declined', color: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300', iconColor: 'text-red-500 dark:text-red-400' },
    failed: { labelKey: 'failed', color: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300', iconColor: 'text-red-500 dark:text-red-400' },
    refunded: { labelKey: 'refunded', color: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300', iconColor: 'text-amber-500 dark:text-amber-400' },
    link_generated: { labelKey: 'linkGenerated', color: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300', iconColor: 'text-blue-500 dark:text-blue-400' },
    released: { labelKey: 'released', color: 'bg-muted text-muted-foreground', iconColor: 'text-muted-foreground' },
};

// Resolves against attempts.methods.* in the 'orders' namespace.
const ATTEMPT_METHOD_KEYS: Record<OrderPaymentAttempt['method'], string> = {
    saved_card: 'savedCard',
    payment_link: 'paymentLink',
};

interface PaymentAttemptGroup {
    /** Log entries backing this attempt, in append order. */
    events: OrderPaymentAttempt[];
    /** The attempt's overall outcome — its most significant event. */
    outcome: OrderPaymentAttempt['event'];
    method: OrderPaymentAttempt['method'];
    at: string;
}

// Most significant first: a refund supersedes paid, paid supersedes approved, etc.
// A released link was closed unpaid, so it outranks the bare link_generated it shares a linkId with.
const ATTEMPT_OUTCOME_PRECEDENCE: OrderPaymentAttempt['event'][] = ['refunded', 'paid', 'approved', 'declined', 'failed', 'released', 'link_generated'];

/**
 * The backend history is an append-only event log, so one attempt spans several entries
 * (e.g. saved-card 'approved' + 'paid', or 'link_generated' + 'paid'). Entries sharing a
 * linkId (or paymentId, for saved cards) are lifecycle events of the same attempt.
 */
function groupPaymentAttempts(attempts: OrderPaymentAttempt[]): PaymentAttemptGroup[] {
    const byKey = new Map<string, OrderPaymentAttempt[]>();
    attempts.forEach((attempt, idx) => {
        const key = attempt.linkId ?? attempt.paymentId ?? `solo-${idx}`;
        const events = byKey.get(key);
        if (events) events.push(attempt);
        else byKey.set(key, [attempt]);
    });
    return Array.from(byKey.values()).map((events) => {
        const outcome = ATTEMPT_OUTCOME_PRECEDENCE.find((e) => events.some((ev) => ev.event === e)) ?? events[events.length - 1].event;
        return { events, outcome, method: events[0].method, at: events[events.length - 1].at };
    });
}

function PaymentAttemptsSection({ attempts }: { attempts: OrderPaymentAttempt[] }) {
    const { t } = useTranslation('orders');
    const [expanded, setExpanded] = useState(false);

    const groups = groupPaymentAttempts(attempts);
    const failedCount = groups.filter((g) => g.outcome === 'declined' || g.outcome === 'failed').length;
    const successCount = groups.filter((g) => g.outcome === 'approved' || g.outcome === 'paid').length;
    const refundedCount = groups.filter((g) => g.outcome === 'refunded').length;

    const summaryParts: string[] = [];
    if (successCount > 0) summaryParts.push(t('attempts.success', { count: successCount }));
    if (failedCount > 0) summaryParts.push(t('attempts.failed', { count: failedCount }));
    if (refundedCount > 0) summaryParts.push(t('attempts.refunded', { count: refundedCount }));
    const summary =
        summaryParts.length > 0
            ? summaryParts.join(', ')
            : t('attempts.attempt', { count: groups.length });

    const hasFailed = failedCount > 0;

    return (
        <div className={`rounded-lg border p-2.5 space-y-2 ${hasFailed ? 'bg-red-50/50 dark:bg-red-950/40 border-red-200 dark:border-red-800/60' : 'bg-muted/30 border-border'}`}>
            <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className="flex items-center justify-between w-full text-left"
            >
                <div className="flex items-center gap-1.5">
                    <RotateCcw className="h-3.5 w-3.5 text-muted-foreground/70" />
                    <span className="text-xs font-medium text-foreground">
                        {t('attempts.title', { count: groups.length })}
                    </span>
                    {hasFailed && (
                        <AlertTriangle className="h-3 w-3 text-red-500 dark:text-red-400" />
                    )}
                </div>
                <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-muted-foreground">{summary}</span>
                    <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`} />
                </div>
            </button>

            {expanded && (
                <div className="space-y-1.5 pt-1 border-t border-border/50">
                    {groups.map((group, idx) => {
                        const config = ATTEMPT_EVENT_CONFIG[group.outcome];
                        return (
                            <div key={idx} className="flex items-start gap-2 text-xs">
                                <span className="text-muted-foreground/70 shrink-0 tabular-nums">
                                    {format(new Date(group.at), 'dd/MM HH:mm')}
                                </span>
                                <div className="flex-1 min-w-0 space-y-0.5">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${config.color}`}>
                                            {t(`attempts.events.${config.labelKey}`)}
                                        </span>
                                        <span className="text-muted-foreground">
                                            {t(`attempts.methods.${ATTEMPT_METHOD_KEYS[group.method]}`)}
                                        </span>
                                    </div>
                                    {group.events.length === 1 ? (
                                        group.events[0].detail && (
                                            <div className="text-muted-foreground/70 truncate" title={group.events[0].detail}>
                                                {group.events[0].detail}
                                            </div>
                                        )
                                    ) : (
                                        <div className="border-l border-border/60 pl-2 space-y-0.5">
                                            {group.events.map((event, eventIdx) => (
                                                <div key={eventIdx} className="flex items-center gap-1.5 text-[10px] text-muted-foreground/70">
                                                    <span className="tabular-nums shrink-0">{format(new Date(event.at), 'dd/MM HH:mm')}</span>
                                                    <span className="shrink-0">
                                                        {t(`attempts.events.${ATTEMPT_EVENT_CONFIG[event.event].labelKey}`)}
                                                    </span>
                                                    {event.detail && (
                                                        <span className="truncate" title={event.detail}>— {event.detail}</span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
